import { useState } from 'react';
import { Alert, App, Button, Card, Space, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/lib/http/apiClient';
import { tariffApi } from '../api/tariffApi';
import { TariffForm } from '../components/TariffForm';
import { toCreateTariffBody, type TariffFormValues } from '../utils/tariffForm';

/**
 * Página de alta de Tarifa (`/tarifas/nueva`, solo `SUPER_ADMIN` — el guard de la
 * ruta lo garantiza). Compone `TariffForm` con el POST y navega al listado en éxito;
 * en error muestra el `message` del backend (ADR 0021).
 */
export function CreateTariffPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const goToList = () => navigate('/tarifas');

  const handleSubmit = async (values: TariffFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      const created = await tariffApi.create(toCreateTariffBody(values));
      message.success('Tarifa creada.');
      navigate(`/tarifas/${encodeURIComponent(created.id)}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se pudo crear la tarifa.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tarifas-page">
      <div className="tarifas-page__header">
        <Space align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al listado de tarifas"
            onClick={goToList}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            Nueva tarifa
          </Typography.Title>
        </Space>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="No se pudo crear la tarifa"
          description={error}
        />
      )}

      <Card>
        <TariffForm
          mode="create"
          submitting={submitting}
          onSubmit={handleSubmit}
          onCancel={goToList}
        />
      </Card>
    </div>
  );
}
